/** Map FastAPI ``detail`` codes to copy suitable for banners and toasts. */
const API_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  no_playbook_found:
    "No playbook matches this contract's type and tags. Ask your admin to create or tag a playbook before assigning policies.",
  no_playbook_version:
    "A playbook exists for this contract, but it has no published version yet. Publish a playbook version before assigning policies.",
  playbook_requires_selection:
    "Select text in the document before adding a clause to the playbook.",
  playbook_assignee_required:
    "Choose someone to review the playbook change.",
  playbook_assignee_not_project_member:
    "The selected playbook reviewer is not a member of this project.",
  playbook_assignee_not_allowed:
    "That person cannot be assigned as the playbook reviewer.",
  playbook_and_signoff_conflict:
    "Use either Sign-off or Update playbook in one comment, not both.",
};

/** Same copy as Apex Ask Cenna when the bridge reports missing ``assistant:use``. */
export const ASSISTANT_USE_DENIED =
  "You don't have permission to use Ask Cenna. Ask an admin to grant you access on your Contract Hub policy.";

const GENERIC_PERMISSION_DENIED =
  "You don't have the required permissions. Ask an admin to update your Contract Hub policy.";

export function userFacingContractHubApiError(raw: string): string {
  const code = raw.trim();
  if (!code) {
    return raw;
  }
  return API_ERROR_MESSAGES[code] ?? code;
}

/**
 * Turn FastAPI ``detail`` (string or ``{ error, missing, message }``) into
 * user-facing copy. Object ``forbidden`` bodies must not be ``JSON.stringify``'d
 * into the chat UI (Word add-in previously showed the raw payload).
 */
export function formatContractHubDetail(detail: unknown): string | null {
  if (typeof detail === "string" && detail.trim()) {
    return userFacingContractHubApiError(detail.trim());
  }
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) {
    return null;
  }
  const d = detail as Record<string, unknown>;
  const missingRaw = d.missing;
  const missing = Array.isArray(missingRaw)
    ? missingRaw.map((m) => String(m)).filter(Boolean)
    : [];

  if (d.error === "forbidden" || missing.length > 0) {
    if (missing.includes("assistant:use")) {
      return ASSISTANT_USE_DENIED;
    }
    const msg = d.message;
    if (typeof msg === "string" && msg.trim() && msg.trim() !== "forbidden") {
      return msg.trim();
    }
    return GENERIC_PERMISSION_DENIED;
  }

  const msg = d.message ?? d.error;
  if (typeof msg === "string" && msg.trim() && msg.trim() !== "forbidden" && msg.trim() !== "error") {
    return msg.trim();
  }
  return null;
}
